import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthLayout } from "@/components/auth/AuthLayout"
import { PasswordInput } from "@/components/auth/PasswordInput"
import { FormAlert } from "@/components/auth/FormAlert"
import { authService } from "@/services/authService"
import { useAuthStore } from "@/store/authStore"
import { afterLoginPath } from "@/lib/pendingInvite"
import { parseDecimalBR } from "@/lib/utils"

export function RegisterPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    monthlyIncome: "",
    currency: "BRL",
  })
  const [confirmPassword, setConfirmPassword] = useState("")
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    if (form.password.length < 8) {
      setError("A senha deve ter ao menos 8 caracteres.")
      return
    }
    if (form.password !== confirmPassword) {
      setError("As senhas não coincidem.")
      return
    }
    if (!acceptTerms) {
      setError("Para criar a conta, aceite os Termos de Uso e a Política de Privacidade.")
      return
    }
    setLoading(true)
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        currency: form.currency,
        monthlyIncome: form.monthlyIncome ? parseDecimalBR(form.monthlyIncome) : undefined,
        acceptTerms,
      }
      const res = await authService.register(payload)
      setAuth(res.user!, res.accessToken)
      navigate(afterLoginPath())
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: Array<{ message: string }>; error?: string } } }
      const apiErrors = axiosErr.response?.data?.errors
      if (apiErrors) {
        setError(apiErrors.map((e) => e.message).join(", "))
      } else {
        setError(axiosErr.response?.data?.error || "Não foi possível criar a conta. Tente novamente.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      pageTitle="Criar conta"
      title="Crie sua conta grátis"
      description="Leva menos de um minuto. Não pedimos cartão de crédito."
      footer={
        <p>
          Já tem conta?{" "}
          <Link to="/login" className="font-medium text-foreground underline underline-offset-2">Entrar</Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name">Nome</Label>
          <Input id="name" autoComplete="name" placeholder="Como você quer ser chamado" value={form.name}
            onChange={(e) => set("name", e.target.value)} required />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">E-mail</Label>
          <Input id="email" type="email" autoComplete="email" placeholder="voce@exemplo.com" value={form.email}
            onChange={(e) => set("email", e.target.value)} required />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Senha</Label>
          <PasswordInput id="password" autoComplete="new-password" placeholder="Mínimo de 8 caracteres"
            value={form.password} onChange={(e) => set("password", e.target.value)} aria-describedby="password-hint" required />
          <p id="password-hint" className="text-[12px] text-muted-foreground">Use 8 caracteres ou mais. Uma frase fácil de lembrar funciona bem.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirmar senha</Label>
          <PasswordInput id="confirmPassword" autoComplete="new-password" placeholder="Repita a senha"
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
          <div className="flex flex-col gap-2">
            <Label htmlFor="monthlyIncome">
              Renda mensal <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input id="monthlyIncome" type="text" inputMode="decimal" placeholder="0,00" className="num"
              value={form.monthlyIncome} onChange={(e) => set("monthlyIncome", e.target.value.replace(/[^0-9.,]/g, ""))} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="currency">Moeda</Label>
            <select id="currency" value={form.currency} onChange={(e) => set("currency", e.target.value)}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm focus:outline-none focus:border-primary">
              <option value="BRL">BRL, real</option>
              <option value="USD">USD, dólar</option>
              <option value="EUR">EUR, euro</option>
            </select>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <input id="acceptTerms" type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-primary" />
          <label htmlFor="acceptTerms" className="text-[13px] leading-snug text-muted-foreground">
            Li e aceito os{" "}
            <Link to="/termos" target="_blank" className="font-medium text-foreground underline underline-offset-2">Termos de Uso</Link>
            {" "}e a{" "}
            <Link to="/privacidade" target="_blank" className="font-medium text-foreground underline underline-offset-2">Política de Privacidade</Link>.
          </label>
        </div>

        {error && <FormAlert>{error}</FormAlert>}

        <Button type="submit" size="lg" className="w-full" disabled={loading}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Criando conta...</> : "Criar conta grátis"}
        </Button>
      </form>
    </AuthLayout>
  )
}
